import { useMemo } from 'react';

import {
  type CustomRoleTaxonomySelection,
  TaxonomyType,
} from '@revelio/data-access';

import { useTaxonomyTree } from '../../../../shared/selection-lists/hooks/useTaxonomyTree';
import type { RoleSelectionListKey } from '../../../../shared/selection-lists/types';
import { getRoleTaxonomyId } from '../../../../shared/selection-lists/utils/roleTaxonomy';
import { getTaxonomyLeafIdsByDepth } from '../../../../shared/selection-lists/utils/taxonomyTree';
import { type Filter, FilterId, isSingleValueFilter } from '../types/filters';
import {
  ROLE_TAXONOMY_SELECTION_LIST_IDS,
  ROLE_TAXONOMY_TREE_LEVELS,
} from '../utils/taxonomy-tree-levels';

export type RoleLeafIds = ReadonlyMap<
  RoleSelectionListKey,
  ReadonlyMap<number, readonly number[]>
>;

const EMPTY_LEAF_IDS: ReadonlyMap<number, readonly number[]> = new Map();

/**
 * Leaf ids for every node of the active role taxonomy, read off the same tree
 * the role filter is built from (same levels, so the same cached request).
 * Undefined while that tree loads.
 */
export const useRoleLeafIds = (filters: Filter[]): RoleLeafIds | undefined => {
  const roleTaxonomyFilter = filters.find(
    (filter) => filter.id === FilterId.ROLE_TAXONOMY
  );
  const roleTaxonomyId = getRoleTaxonomyId(
    roleTaxonomyFilter &&
      isSingleValueFilter<CustomRoleTaxonomySelection>(roleTaxonomyFilter)
      ? roleTaxonomyFilter.value
      : undefined
  );

  const { taxonomy, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId: roleTaxonomyId,
    levels: ROLE_TAXONOMY_TREE_LEVELS,
    pause: roleTaxonomyId === undefined,
  });

  return useMemo(() => {
    if (fetching) return undefined;

    const leafIdsByDepth = getTaxonomyLeafIdsByDepth(taxonomy);
    return new Map(
      ROLE_TAXONOMY_SELECTION_LIST_IDS.map((levelId, depth) => [
        levelId,
        leafIdsByDepth[depth] ?? EMPTY_LEAF_IDS,
      ])
    );
  }, [fetching, taxonomy]);
};
